import { CrmLead, CrmTask, STAGES, WON_STAGES, brl, dateTimeBR, daysSince, isSameDay } from "@/lib/crm";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle, CalendarClock, Clock, FileWarning, Flame, Handshake,
  TrendingUp, Users,
} from "lucide-react";

interface Props {
  leads: CrmLead[];
  tasks: CrmTask[];
  onOpenLead: (lead: CrmLead) => void;
}

const Metric = ({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "default" | "gold" | "hot" | "muted";
}) => (
  <Card
    className={`p-4 border ${
      tone === "gold"
        ? "border-accent/60 bg-accent/5"
        : tone === "hot"
        ? "border-destructive/40 bg-destructive/5"
        : "border-border"
    }`}
  >
    <p className="text-[11px] font-body font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </p>
    <p className="font-display text-2xl font-bold text-foreground mt-1 leading-tight">{value}</p>
    {hint && <p className="text-xs text-muted-foreground font-body mt-0.5">{hint}</p>}
  </Card>
);

const PriorityBlock = ({
  icon,
  title,
  leads,
  onOpenLead,
  tone,
}: {
  icon: React.ReactNode;
  title: string;
  leads: CrmLead[];
  onOpenLead: (l: CrmLead) => void;
  tone?: "danger";
}) => (
  <Card className="p-4 border border-border">
    <div className="flex items-center gap-2 mb-3">
      <span className={tone === "danger" ? "text-destructive" : "text-accent"}>{icon}</span>
      <h4 className="font-display text-sm font-bold text-foreground">{title}</h4>
      <Badge variant="outline" className="ml-auto font-body">
        {leads.length}
      </Badge>
    </div>
    {leads.length === 0 ? (
      <p className="text-xs text-muted-foreground font-body">Nada pendente aqui. 👌</p>
    ) : (
      <ul className="space-y-1 max-h-48 overflow-y-auto">
        {leads.slice(0, 12).map((l) => (
          <li key={l.id}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenLead(l)}
              className="w-full justify-start font-body text-sm h-8"
            >
              <span className="truncate">{l.client_name}</span>
              <span className="ml-auto text-xs text-muted-foreground shrink-0">
                {l.follow_up_at ? dateTimeBR(l.follow_up_at) : `${daysSince(l.last_contact_at)}d`}
              </span>
            </Button>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

const CrmDashboard = ({ leads, tasks, onOpenLead }: Props) => {
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - now.getDay());
  startOfWeek.setHours(0, 0, 0, 0);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  const byClass = (c: string) => leads.filter((l) => l.classification === c);
  const inStage = (s: string) => leads.filter((l) => l.stage === s);

  const won = leads.filter((l) => WON_STAGES.includes(l.stage));
  const wonMonth = won.filter((l) => new Date(l.closed_at || l.updated_at) >= startOfMonth);
  const wonYear = won.filter((l) => new Date(l.closed_at || l.updated_at) >= startOfYear);
  const sum = (list: CrmLead[]) => list.reduce((t, l) => t + (l.deal_value || 0), 0);

  const ticket = won.length ? sum(won) / won.length : 0;
  const conversion = leads.length ? (won.length / leads.length) * 100 : 0;
  const avgDays = won.length
    ? Math.round(
        won.reduce(
          (t, l) =>
            t +
            Math.max(
              0,
              (new Date(l.closed_at || l.updated_at).getTime() - new Date(l.created_at).getTime()) /
                86400000,
            ),
          0,
        ) / won.length,
      )
    : 0;

  const leadTasks = (leadId: string) => tasks.filter((t) => t.lead_id === leadId && !t.is_done);

  const followToday = leads.filter(
    (l) =>
      (!l.follow_up_done && isSameDay(l.follow_up_at)) ||
      leadTasks(l.id).some((t) => isSameDay(t.due_at)),
  );
  const followLate = leads.filter(
    (l) =>
      (!l.follow_up_done && l.follow_up_at && new Date(l.follow_up_at) < now && !isSameDay(l.follow_up_at)) ||
      leadTasks(l.id).some((t) => new Date(t.due_at) < now && !isSameDay(t.due_at)),
  );
  const noContact7 = leads.filter(
    (l) => l.stage !== "perdido" && daysSince(l.last_contact_at || l.created_at) > 7,
  );
  const noTask = leads.filter(
    (l) => l.stage !== "perdido" && leadTasks(l.id).length === 0 && !l.follow_up_at,
  );
  const visitsToday = leads.filter((l) => isSameDay(l.visit_date));
  const docsPending = inStage("documentacao");
  const proposalsWaiting = [...inStage("proposta"), ...inStage("contra_proposta")];
  const veryHot = byClass("muito_quente");

  return (
    <div className="space-y-8">
      {/* Metrics */}
      <div>
        <h3 className="font-display text-base font-bold text-foreground mb-3 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-accent" /> Visão executiva
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <Metric label="Leads hoje" value={leads.filter((l) => isSameDay(l.created_at)).length} tone="gold" />
          <Metric label="Leads na semana" value={leads.filter((l) => new Date(l.created_at) >= startOfWeek).length} />
          <Metric label="Leads no mês" value={leads.filter((l) => new Date(l.created_at) >= startOfMonth).length} />
          <Metric label="Clientes quentes" value={byClass("quente").length + veryHot.length} tone="hot" />
          <Metric label="Clientes mornos" value={byClass("morno").length} />
          <Metric label="Clientes frios" value={byClass("frio").length} />
          <Metric label="Perdidos" value={inStage("perdido").length} />
          <Metric label="Visitas agendadas" value={leads.filter((l) => l.visit_date).length} />
          <Metric label="Visitas de hoje" value={visitsToday.length} tone="gold" />
          <Metric label="Propostas em aberto" value={proposalsWaiting.length} />
          <Metric label="Contratos" value={inStage("contrato").length} />
          <Metric label="Escrituras" value={inStage("escritura").length} />
          <Metric label="Vendas concluídas" value={won.length} tone="gold" />
          <Metric label="Vendido no mês" value={brl(sum(wonMonth))} />
          <Metric label="Vendido no ano" value={brl(sum(wonYear))} />
          <Metric label="Ticket médio" value={brl(ticket)} />
          <Metric label="Taxa de conversão" value={`${conversion.toFixed(1)}%`} />
          <Metric label="Tempo médio até a venda" value={`${avgDays} dias`} />
        </div>
      </div>

      {/* Priorities */}
      <div>
        <h3 className="font-display text-base font-bold text-foreground mb-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-accent" /> Painel de prioridades
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <PriorityBlock icon={<CalendarClock className="h-4 w-4" />} title="Follow ups de hoje" leads={followToday} onOpenLead={onOpenLead} />
          <PriorityBlock icon={<Clock className="h-4 w-4" />} title="Follow ups atrasados" leads={followLate} onOpenLead={onOpenLead} tone="danger" />
          <PriorityBlock icon={<Users className="h-4 w-4" />} title="Sem contato há +7 dias" leads={noContact7} onOpenLead={onOpenLead} tone="danger" />
          <PriorityBlock icon={<AlertTriangle className="h-4 w-4" />} title="Sem próxima tarefa" leads={noTask} onOpenLead={onOpenLead} tone="danger" />
          <PriorityBlock icon={<CalendarClock className="h-4 w-4" />} title="Visitas de hoje" leads={visitsToday} onOpenLead={onOpenLead} />
          <PriorityBlock icon={<FileWarning className="h-4 w-4" />} title="Documentações pendentes" leads={docsPending} onOpenLead={onOpenLead} />
          <PriorityBlock icon={<Handshake className="h-4 w-4" />} title="Propostas aguardando" leads={proposalsWaiting} onOpenLead={onOpenLead} />
          <PriorityBlock icon={<Flame className="h-4 w-4" />} title="Leads muito quentes" leads={veryHot} onOpenLead={onOpenLead} />
        </div>
      </div>

      {/* Funnel snapshot */}
      <div>
        <h3 className="font-display text-base font-bold text-foreground mb-3">Funil comercial</h3>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((s) => {
            const n = inStage(s.value).length;
            return (
              <div
                key={s.value}
                className={`rounded-md border px-3 py-2 font-body text-xs ${
                  n > 0 ? "border-accent/50 bg-accent/5 text-foreground" : "border-border text-muted-foreground"
                }`}
              >
                {s.label} <span className="font-semibold">{n}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CrmDashboard;
